import findIndex from 'lodash/findIndex';

export const toHeatMapCategories = (data: any | undefined, size: number) => {
  if (data !== undefined && Object.keys(data).length === size) {
    const ids: number[] = [];
    const keys = Object.keys(data);
    keys.forEach(key =>
      data[key].forEach((v: { id: number }) => {
        ids.push(v.id);
      }),
    );
    return [...new Set([...ids])];
  }
  return [];
};

export const toHeatMapSeriesWithIdAsYAxis = (data: any | undefined, decimals = 2, size: number) => {
  const heatMapSeries: any[][] = [];
  try {
    if (data !== undefined && Object.keys(data).length === size) {
      const categories = toHeatMapCategories(data, size); // use id field as y-axis categories
      const keys = Object.keys(data);
      keys.forEach((key, xIndex) =>
        data[key].forEach((v: { id: number; value: number; name: any }) => {
          const yIndex = findIndex(categories, n => n === v.id);
          const value = v.value ? Number(v.value.toFixed(decimals)) : '-';
          heatMapSeries.push([xIndex, yIndex, value, v.id, v.name]);
        }),
      );
    }
  } catch (e) {}
  return heatMapSeries;
};

export const toHeatMapSeries = (data: any | undefined, decimals = 2, size: number) => {
  const heatMapSeries: any[][] = [];
  try {
    if (
      data !== undefined &&
      Object.keys(data).length === size &&
      !Object.values(data)
        .flat()
        .some(obj => obj === undefined)
    ) {
      const keys = Object.keys(data);
      keys.forEach((key, xIndex) =>
        data[key].forEach((v: { value: number; id: any; name: any }, yIndex: any) => {
          const value = v.value ? Number(v.value.toFixed(decimals)) : '-';
          heatMapSeries.push([xIndex, yIndex, value, v.id, v.name]);
        }),
      );
    }
  } catch (e) {}
  return heatMapSeries;
};

// process heatmap data, use higher value of infra or aws source and add source to the returned data as [x, y, value, id, type]
enum DataSource {
  INFRA_AGENT = 'infra-agent',
  AWS_CLOUDWATCH = 'aws-cloudwatch',
}
type MatrixItem = [number, number, number | null, string, string]; // [x, y, value, id, type]
interface DataItem {
  id: string;
  name: string;
  infra: number | null;
  aws: number | null;
}
export const toHeatMapSeriesTiles = (data: DataItem[], columns: number): MatrixItem[] => {
  data.sort((a, b) => {
    const aMax = Math.max(a.infra ?? -Infinity, a.aws ?? -Infinity);
    const bMax = Math.max(b.infra ?? -Infinity, b.aws ?? -Infinity);
    return bMax - aMax;
  });

  const matrix: MatrixItem[] = [];
  let currentX = 0;
  let currentY = 0;

  data.forEach(item => {
    const maxValue = Math.max(item.infra ?? -Infinity, item.aws ?? -Infinity);
    // this is only for heatmap tooltip and will always show the actual source that was used in the sorting
    const type: DataSource =
      (item.infra !== null && item.aws !== null && item.infra >= item.aws) || (item.infra === null && item.aws === null)
        ? DataSource.INFRA_AGENT
        : DataSource.AWS_CLOUDWATCH;

    matrix.push([currentX, currentY, maxValue !== -Infinity ? maxValue : null, item.id, type]);

    currentX++;

    if (currentX >= columns) {
      currentX = 0;
      currentY++;
    }
  });

  return matrix;
};

export const getTopInstanceIdsByMetricValue = (data: DataItem[] = [], top: number): [string, string][] => {
  const sortedData = [...data].sort((a, b) => {
    const aMax = Math.max(a.infra ?? -Infinity, a.aws ?? -Infinity);
    const bMax = Math.max(b.infra ?? -Infinity, b.aws ?? -Infinity);
    return bMax - aMax;
  });

  const topInstances = sortedData.slice(0, top).map(item => {
    // source is 'infra-agent' even if this instance is in the top because of higher value from 'aws-cloudwatch'
    const source = item.infra !== null || item.aws === null ? DataSource.INFRA_AGENT : DataSource.AWS_CLOUDWATCH;
    return [item.id, source] as [string, string];
  });

  return topInstances;
};
